import _capitalize from 'lodash/capitalize';
import EntryWhereSomeInput from '@src/components/manage/EntryWhereSomeInput';
import gql from 'graphql-tag';
import formatDate from 'date-fns/format';
import LocationSearch from '@/components/misc/LocationSearch';
import { bRowCols, bForm, bFormGroup } from '@src/plugins/vue-form-bricks/bootstrap-vue';
import { ContentSharesWhereManyInput } from '@src/models/where';

export const RelationContentSharesFragment = gql`
  fragment RelationContentSharesDetails on ContentShare {
    id
    atsID
    title
    description
    url
    domain
    createdAt
    status
    image {
      id
      fileLocation
    }
    customFields
    imageURL
  }
`;

export const listSchema = {
  page: {
    title: 'Manage Content Shares',
    breadcrumb: 'Content Shares',
  },
  query: gql`
    query ListContentSharesEntries($where: ContentShareWhereManyInput, $order: ContentShareOrderByInput) {
      company {
        id
        contentShareConnections(where: $where, order: $order) {
          edges {
            entry {
              ...RelationContentSharesDetails
            }
          }
          total
        }
      }
    }
    ${RelationContentSharesFragment}
  `,
  update({ company: { contentShareConnections: { edges, total } = {} } = {} } = {}) {
    return {
      edges: (Array.isArray(edges) ? edges : []).map(({ entry }) => entry),
      total,
    };
  },
  where: { status: 'ACTIVE' },
  whereOptions: ContentSharesWhereManyInput,
  order: 'CREATED_ASC',
  orderOptions: () => ({
    'CREATED_DESC': 'Newest first',
    'CREATED_ASC': 'Oldest first',
    'UPDATED_DESC': 'Recently updated',
    'UPDATED_ASC': 'Least updated',
  }),
  primaryKey: 'id',
  columns() {
    const columns = [
      { key: 'title', label: 'Name' },
      { key: 'atsID', label: 'ATS ID' },
      { key: 'domain', label: 'Domain' },
    ];

    if (!this.modalOpts) {
      columns.push({ key: 'createdAt', formatter: value => formatDate(value, 'DD/MM/YYYY HH:MM') });
      columns.push({ key: 'status', formatter: value => _capitalize(value) });
    }

    return columns;
  },
  option(createElement, { id, title }) {
    return createElement('div', { class: 'd-flex flex-row justify-content-start align-items-center' }, [
      createElement('span', { class: 'bg-dark text-white rounded-circle mr-1' }, [
        createElement('font-awesome-icon', { class: 'mx-1', props: { icon: 'link', fixedWith: true, size: 'sm' } }),
      ]),
      createElement('span', { class: 'd-block' }, title),
    ]);
  },
  emptyOptionValue() {
    return {
      icon: this.many ? 'user' : 'users',
      title: this.many ? 'Select Content Shares' : 'Select Content Share',
    };
  },
};

export const editSchema = {
  page: {
    title() {
      return this.value && this.value.fullName
        ? `Edit Content Share - ${this.value.fullName}`
        : (this.entryID === 'new' ? 'New Content Share' : 'Edit Content Share');
    },
    breadcrumb() {
      return this.entryID === 'new' ? 'New' : 'Edit';
    },
  },
  apolloReadEntry: {
    query: gql`
      query ReadContentSharesEntry($entryID: ID!) {
        company {
          id
          value: job(where:{ id: $entryID }) {
            ...ManageContentSharesDetails
          }
        }
      }
    `,
  },
  apolloCreateEntry: {
    mutation: gql`
      mutation CreateContentSharesEntry($entryID: ID!, $create: CreateContentSharesInput!) {
        value: createContentShares(create: $create) {
          ...ManageContentSharesDetails
        }
      }
    `,
  },
  apolloUpdateEntry: {
    mutation: gql`
      mutation UpdateContentSharesEntry($entryID: ID!, $update: UpdateContentSharesInput!) {
        value: updateContentShares(update: $update, where: { id: $entryID }) {
          ...ManageContentSharesDetails
        }
      }
    `,
  },
  form: {
    $main: bForm({}, {
      contentBlock: bRowCols({ props: { cols: 12 } }, {
        title: bFormGroup({ props: { label: 'Title', 'label-for': 'title' } }, {
          is: 'b-input',
          props: { 'v-model': '$value.title' },
          domProps: { id: 'title' },
        }),
        description: bFormGroup({ props: { label: 'Description', 'label-for': 'description' } }, {
          is: 'b-form-textarea',
          props: {
            'no-resize': true,
            'no-auto-shrink': true,
            rows: 10,
            'v-model': '$value.description',
          },
          domProps: { id: 'description' },
        }),
      }),
      $hr1: { is: 'hr' },
      atsBlock: bRowCols({ props: { cols: 12, lg: 6 } }, {
        atsID: bFormGroup({ props: { label: 'ATS ID', 'label-for': 'atsID', 'label-cols-sm': 3 } }, {
          input: { is: 'b-input', props: { 'v-model': '$value.atsID' }, domProps: { id: 'atsID' } },
          help: {
            is: 'b-form-text',
            children: 'Set by your ATS & should not be edited by hand',
          },
        }),
        atsURL: bFormGroup({ props: { label: 'ATS URL', 'label-for': 'atsURL', 'label-cols-sm': 3 } }, {
          input: { is: 'b-input', props: { 'v-model': '$value.atsURL' }, domProps: { id: 'atsURL' } },
          help: {
            is: 'b-form-text',
            children: 'Enter a URL here to automatically redirect to an ATS',
          },
        }),
        // divide: { is: 'div', domProps: { class: 'w-100' } },
        // @TODO Add customFieldsHash editor here!
      }),
      $hr2: { is: 'hr' },
      detailsBlock: bRowCols({ props: { cols: 12, lg: 6 } }, {
        department: bFormGroup({ props: { label: 'Department', 'label-for': 'department', 'label-cols-sm': 3 } }, {
          is: EntryWhereSomeInput,
          props: {
            type: 'departments',
            title: 'Department',
            where: { status: 'ACTIVE' },
            'v-model': '$value.department',
          },
          domProps: { id: 'department' },
          on: {
            input(ctx, department) {
              ctx.setData('$value.department', department && department.id ? { id: department.id } : null);
            },
          },
        }),
        location: bFormGroup({ props: { label: 'Location', 'label-for': 'location', 'label-cols-sm': 3 } }, {
          is: LocationSearch,
          props: { value: '$value.location' },
          domProps: { id: 'location' },
          on: {
            input(ctx, location) {
              ctx.setData('$value.location', location && location.id ? { id: location.id } : null);
            },
          },
        }),
        industry: bFormGroup({ props: { label: 'Industry', 'label-for': 'industry', 'label-cols-sm': 3 } }, {
          is: 'b-input',
          props: { 'v-model': '$value.industry' },
          domProps: { id: 'industry' },
        }),
        referralBonus: bFormGroup({ props: { label: 'Referral Bonus', 'label-for': 'referralBonus', 'label-cols-sm': 3 } }, {
          is: 'b-input',
          props: { 'v-model': '$value.referralBonus' },
          domProps: { id: 'referralBonus' },
        }),
        image: bFormGroup({ props: { label: 'Image', 'label-for': 'image', 'label-cols-sm': 3 } }, {
          is: 'b-input',
          props: { 'v-model': '$value.image' },
          domProps: { id: 'image' },
        }),
      }),
    }),
    $sidebar: {
      props: {
        data: {
          type: Object,
          default: () => null,
        },
        value: {
          type: Object,
          default: () => ({}),
        },
      },
      render(el) {
        return el('b-table', {
          class: 'status-table',
          props: {
            stacked: true,
            fields: [
              { key: 'id', label: 'ID' },
              { key: 'createdAt', label: 'Created At', formatter: value => formatDate(value, 'DD/MM/YYYY HH:MM') },
              { key: 'updatedAt', label: 'Updated At', formatter: value => formatDate(value, 'DD/MM/YYYY HH:MM') },
              { key: 'status', label: 'Status', formatter: value => _capitalize(`${value}`.toLowerCase()) },
            ],
            items: this.value ? [ this.value ] : [],
          },
        });
      },
    },
  },
};



// WEBPACK FOOTER //
// ./src/models/ContentShares.js